var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ValidateToken } from './IValidate.js';
import { services } from './Services.js';
/**
 * `QboValidate` component is responsible for performing validation on forms and inputs.
 * It emits two custom events to indicate the completion of validation processes:
 * - `qbo-form-validated`: Fired when the entire form has been validated.
 * - `qbo-input-validated`: Fired when a single input within the form has been validated.
 *
 * @fires qbo-form-validated - Event fired when the form validation is complete.
 * @fires qbo-input-validated - Event fired when an individual input validation is complete.
 * @example
 * ```html
 * <form id="myForm">
 *   <qbo-validate></qbo-validate>
 * </form>
 * ```
 * ```javascript
 * document.querySelector('qbo-validate').addEventListener('qbo-form-validated', (e) => {
 *   console.log('Form validation completed');
 * });
 * document.querySelector('qbo-validate').addEventListener('qbo-input-validated', (e) => {
 *   console.log('Input validation completed');
 * });
 * ```
 */
let QboValidate = class QboValidate extends LitElement {
    constructor() {
        super();
        this.renderInHost = false;
        this.autoComplete = 'off';
        this.validatedClass = 'was-validated';
        this.workingClass = ['bg-light', 'text-secondary'];
        this.form = null;
        this.validators = [];
        this.validate = async (event) => {
            console.trace('validating form');
            event.preventDefault();
            const targets = Array.from(this.form?.querySelectorAll('*'));
            for (const target of targets) {
                if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) {
                    console.trace('validating element ', target);
                    await this.validateElement(target);
                }
                else
                    console.trace('ignoring element ', target);
            }
            if (!this.form?.checkValidity()) {
                console.trace('form is invalid');
                event.preventDefault();
                event.stopPropagation();
            }
            else {
                console.trace('form is valid, js submit');
                // this.form.submit();
            }
            this.form?.classList.add(this.validatedClass);
            this.dispatchEvent(new Event('qbo-form-validated', { bubbles: true }));
        };
        this.validateElement = async (source) => {
            const target = source instanceof Event ? source.target : source;
            console.trace('validateElement start ', target);
            if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) {
                target.setCustomValidity('');
                // Re-verify build-in validation.
                if (!target.checkValidity())
                    return;
                for (const validator of this.validators) {
                    if (target.matches(validator.selector)) {
                        console.trace('element matches selector ', validator.selector, ' for ', target.matches(validator.selector) ? 'true' : 'false');
                        if (!target.checkValidity())
                            break;
                        // Show some loading indication if necessary.
                        this.workingClass.forEach(c => target.classList.add(c));
                        // Validate the element.
                        const valid = await validator.validate(target);
                        // Clear loading indication.
                        this.workingClass.forEach(c => target.classList.remove(c));
                        // Set custom validity based on the validation result.
                        target.setCustomValidity(valid ? '' : validator.message);
                        console.trace('input validity is ', target.checkValidity(), ' message is ', target.validationMessage);
                    }
                    else
                        console.trace('element does not match selector ', validator.selector, ' for ', target.matches(validator.selector) ? 'true' : 'false');
                }
            }
            console.trace('validateElement end ', target);
            target?.dispatchEvent(new Event('qbo-input-validated', { bubbles: true }));
        };
        // Create a child container specific to this instance of QboValidate
        this.childContainer = services.container.createChildContainer();
    }
    async connectedCallback() {
        super.connectedCallback();
        this.form = this.closest('form');
        if (this.form == null)
            return;
        this.form?.addEventListener('submit', this.validate);
        this.form?.addEventListener('change', this.validateElement);
        this.childContainer.registerInstance(HTMLFormElement, this.form);
        this.validators = this.childContainer.resolveAll(ValidateToken);
        this.validators.forEach(v => v.connect(this.form));
    }
    async disconnectedCallback() {
        super.disconnectedCallback();
        this.validators.forEach(v => v.disconnect());
        this.form?.removeEventListener('change', this.validateElement);
        this.form?.removeEventListener('submit', this.validate);
    }
    render() {
        return html ``;
    }
};
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], QboValidate.prototype, "renderInHost", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", Object)
], QboValidate.prototype, "autoComplete", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", Object)
], QboValidate.prototype, "validatedClass", void 0);
__decorate([
    property({
        converter: {
            fromAttribute: (value, type) => value?.split(' '),
            toAttribute: (value, type) => value.join(' ')
        }
    }),
    __metadata("design:type", Object)
], QboValidate.prototype, "workingClass", void 0);
QboValidate = __decorate([
    customElement('qbo-validate'),
    __metadata("design:paramtypes", [])
], QboValidate);
export { QboValidate };
